import { z } from 'zod';
/**
 * THE shared list-query DTO + paginated-response envelope (E6/E17, §5.9).
 *
 * Server-side pagination/sort/search is MANDATORY for anything backed by a
 * queryable entity — fetch-all-then-filter is a review block. Every list op's
 * request extends `listQueryInput(<sortColumns>)`; every list op's response is
 * `paginated(<rowSchema>)`.
 *
 * `sort` is an ENUM of allowed columns (the injection defence — the server maps
 * the enum value to a known Drizzle column, never interpolates a raw name).
 */
export const sortDir = z.enum(['asc', 'desc']);
/** Hard cap on page size across the whole product (§5.9). */
export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 25;
/**
 * Build a resource's list-query schema. `page` XOR `cursor` per resource
 * (offset paging on desktop tables, cursor "Load more" on mobile — E4
 * `<DataTable>`). `pageSize` is clamped by the schema to `[1, maxPageSize]`.
 */
export function listQueryInput(sortColumns, opts) {
    const max = Math.min(opts.maxPageSize ?? MAX_PAGE_SIZE, MAX_PAGE_SIZE);
    const size = opts.defaultPageSize ?? DEFAULT_PAGE_SIZE;
    return z.object({
        page: z.number().int().min(1).default(1),
        cursor: z.string().optional(),
        pageSize: z.number().int().min(1).max(max).default(size),
        sort: z.enum(sortColumns).default(opts.defaultSort),
        dir: sortDir.default(opts.defaultDir ?? 'desc'),
        q: z.string().trim().max(200).optional(),
    });
}
/**
 * Paginated response envelope. `total` is null when cursor-paged; `nextCursor`
 * is null on the last page.
 */
export function paginated(row) {
    return z.object({
        rows: z.array(row),
        total: z.number().int().nullable(),
        nextCursor: z.string().nullable(),
        page: z.number().int(),
        pageSize: z.number().int(),
    });
}
